import React from 'react'
import { compose } from '@cbgms/base/utils/compose'
import { FormSubmitHandler, SubmitHandler } from 'redux-form'
import { Button, Modal } from 'semantic-ui-react'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'

type ShowDialogOnSaveFunc = (props: any) => boolean
type AsyncShowDialogOnSaveFunc = (props: any) => Promise<boolean>
type ReduxFormFunc = (values: any, dispatch: any, props: any) => void

interface Config {
  showDialogOnSave?: boolean | ShowDialogOnSaveFunc | AsyncShowDialogOnSaveFunc
  dialogText?: string
  dialogTitle?: string
}

export interface WithSaveConfirmationModalProps extends Config {
  handleSubmit?: SubmitHandler
  onSubmit?: FormSubmitHandler | SubmitHandler
}

export interface WithSaveConfirmationModalOuterProps {
  handleSubmit: SubmitHandler
  onSubmit: FormSubmitHandler<any, any>
  handleConfirmSubmit: (callback: ReduxFormFunc) => () => void
}

export const withSaveConfirmationModalRedux = (config?: Config) =>
  compose(
    connect(
      (state: any, props: WithSaveConfirmationModalProps & { form: string }) => ({
        /* Cant use application State interface, find redux form interface for state? */
        ...state.form[props.form]
      }),
      null
    ),
    withSaveConfirmationModal(config)
  )

export const withSaveConfirmationModal =
  (config: Config = {}) =>
  (WrappedComponent: React.ComponentType<Partial<WithSaveConfirmationModalOuterProps>>) =>
  (props: WithSaveConfirmationModalProps) => {
    const {
      dialogText = config.dialogText,
      dialogTitle = config.dialogTitle,
      showDialogOnSave = config.showDialogOnSave,
      handleSubmit = ((cb: () => void) => () => cb()) as SubmitHandler
    } = props

    const { t } = useTranslation()

    const [dialogVisible, setDialogVisible] = React.useState(false)
    const [handler, setHandler] = React.useState<(() => void) | undefined>(undefined)

    const handleConfirmSubmit = (callback?: ReduxFormFunc) => {
      if (!callback) return () => {}
      return async () => {
        const showDialog = typeof showDialogOnSave === 'function' ? await showDialogOnSave(props) : showDialogOnSave

        if (showDialog) {
          setDialogVisible(true)
          setHandler(() => callback)
        } else {
          handleSubmit(callback)()
        }
      }
    }

    const onSubmit = handleConfirmSubmit(props.onSubmit)

    const onDecline = () => {
      setDialogVisible(false)
      setHandler(undefined)
    }

    const onSubmitComplete = () => {
      if (!handler) {
        return
      }

      handleSubmit(handler)()

      setHandler(undefined)
      setDialogVisible(false)
    }

    return (
      <>
        {dialogVisible && (
          <Modal data-testid='save-confirmation-modal' size='small' open closeOnEscape closeOnDimmerClick onClose={onDecline} dimmer>
            <Modal.Header>{t(dialogTitle || 'save_confirmation.title')}</Modal.Header>
            {dialogText && <Modal.Content>{t(dialogText)}</Modal.Content>}
            <Modal.Actions>
              <Button data-testid='save-confirmation-decline' negative content={t('save_confirmation.decline')} onClick={onDecline} />
              <Button data-testid='save-confirmation-accept' positive content={t('save_confirmation.accept')} onClick={onSubmitComplete} />
            </Modal.Actions>
          </Modal>
        )}
        <WrappedComponent {...props} handleSubmit={handleSubmit} handleConfirmSubmit={handleConfirmSubmit} onSubmit={onSubmit} />
      </>
    )
  }

export default withSaveConfirmationModalRedux
